/**
 * Demo-state signals carried on every API response (plan docs/data-migration/03).
 * Pure so `npm run test:unit` covers them; `lib/api-client.ts` feeds them and
 * `components/layout/DemoStateWatcher.tsx` shows them.
 *
 * - `X-Demo-State-Generation` changes when a Master Reset commits. A tab that saw an
 *   older generation is looking at rows that may no longer exist, so it offers a reload.
 * - While a reset runs, write requests answer `503` with `Retry-After`.
 */

export const DEMO_STATE_GENERATION_HEADER = 'X-Demo-State-Generation'

/** The backend's maintenance message; used when a 503 carries no readable detail. */
export const MAINTENANCE_MESSAGE = 'Demo data is being reset, try again in a minute.'

export type DemoStateEvent =
  | { type: 'maintenance'; message: string; retryAfterSeconds: number | null }
  | { type: 'generation-changed'; from: string; to: string }

/**
 * Remembers the first generation this tab saw and reports a change once per new value.
 * A missing header (older backend, or a response CORS hides it on) is ignored.
 *
 * Generations only grow, so a numeric value at or below the highest one already seen is
 * ignored: right after a reset, each API worker caches its generation for a few seconds,
 * and a tab that reaches an updated worker and then a stale one must not be told the data
 * went back from N+1 to N.
 */
export function createGenerationTracker() {
  let first: string | null = null
  let lastReported: string | null = null
  let highest: number | null = null
  return {
    observe(value: string | null | undefined): { from: string; to: string } | null {
      const next = value?.trim()
      if (!next) return null
      const numeric = /^\d+$/.test(next) ? Number(next) : null
      if (first === null) {
        first = next
        highest = numeric
        return null
      }
      if (numeric !== null && highest !== null) {
        if (numeric <= highest) return null
        highest = numeric
      }
      if (next === first || next === lastReported) return null
      lastReported = next
      return { from: first, to: next }
    },
    /** The generation this tab started on, or null before the first response. */
    get baseline(): string | null {
      return first
    },
  }
}

/** `Retry-After` in seconds; accepts delta-seconds or an HTTP date, null when absent or unreadable. */
export function parseRetryAfter(header: string | null | undefined, now = Date.now()): number | null {
  if (!header) return null
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  const at = Date.parse(trimmed)
  if (Number.isNaN(at)) return null
  return Math.max(0, Math.round((at - now) / 1000))
}

/**
 * Whether a 503 is the reset's maintenance answer rather than another unavailable route
 * (the signed machine routes also use 503, but the browser never calls them).
 */
export function isMaintenanceResponse(status: number, retryAfter: string | null, detail: string | null): boolean {
  if (status !== 503) return false
  if (retryAfter) return true
  return !!detail && detail.toLowerCase().startsWith('demo data is being reset')
}
